import { PageDS, ToastContainerDS } from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import "../../../../i18n/i18n";
import { useNavigate } from "react-router-dom";
import { AddUserRole } from "./addUserRole";
import { ManagerCreation, ManagerCreationRole } from "../usersAPI";
import { AddUserInformations } from "./addUserInformation";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { userAsync } from "../usersSlice";
import { AddUserDepartmentModal } from "./addUserDepartmentModal";
import { DepartmentType } from "../../departments/departmentsApi";
import useFullScreen from "../../../../hooks/useFullScreen";

type Steps = "role" | "informations" | "department";

export type UserInformations = {
  firstName: string;
  lastName: string;
  email: string;
  job: string;
};

export const AddUserPage = () => {
  useFullScreen();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Steps>("informations");
  const company = useAppSelector(selectCompany);

  const [userRole, setUserRole] = useState<ManagerCreationRole | null>(null);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [job, setJob] = useState<string>("");
  const [userDepartments, setUserDepartments] = useState<DepartmentType[]>([]);

  const resetValue = useCallback(() => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setJob("");
    setUserRole(null);
    setUserDepartments([]);
  }, []);

  const onClose = useCallback(() => {
    resetValue();
    navigate("/home/users/management");
  }, [navigate, resetValue]);

  const onSubmitInformations = useCallback(
    (informations: UserInformations) => {
      setFirstName(informations.firstName);
      setLastName(informations.lastName);
      setEmail(informations.email);
      setJob(informations.job);
      setStep("role");
    },
    [setFirstName, setLastName, setEmail, setJob],
  );

  const isValidForm = useMemo(() => {
    return !!(firstName && lastName && email && userRole);
  }, [firstName, lastName, email, userRole]);

  const onSubmit = useCallback(() => {
    if (!isValidForm || !company || !userRole) return;
    if (userRole === "DEPARTMENT_USER" && userDepartments.length === 0) return;
    const userData: ManagerCreation = {
      firstName,
      lastName,
      email,
      job,
      companyUuid: company.uuid,
      role: userRole,
      requestedAt: new Date(),
      departmentsAccess: userDepartments.map((department) => department.uuid),
    };

    resetValue();
    onClose();
    void dispatch(userAsync(userData));
  }, [
    company,
    dispatch,
    email,
    firstName,
    isValidForm,
    job,
    lastName,
    onClose,
    resetValue,
    userRole,
    userDepartments,
  ]);

  const onSubmitUserRole = useCallback(
    (role: "ADMIN" | "USER" | "DEPARTMENT_USER") => {
      setUserRole(role);
      if (role !== "DEPARTMENT_USER") onSubmit();
      else setStep("department");
    },
    [onSubmit],
  );

  const onSubmitUserDepartment = useCallback(
    (departments: DepartmentType[]) => {
      setUserDepartments(departments);
      onSubmit();
    },
    [onSubmit],
  );

  return (
    <PageDS
      isFullScreen
      title={t("users.modal.title") || ""}
      toaster={<ToastContainerDS />}
      titleElement={{
        closeButtonAction: onClose,
      }}
      withoutSupport
    >
      {step === "informations" ? (
        <AddUserInformations
          onSubmit={onSubmitInformations}
          firstName={firstName}
          lastName={lastName}
          email={email}
          job={job}
        />
      ) : (
        <></>
      )}
      {step === "role" ? (
        <AddUserRole
          onSubmit={onSubmitUserRole}
          onReturn={() => setStep("informations")}
        />
      ) : (
        <></>
      )}
      {step === "department" ? (
        <AddUserDepartmentModal
          onReturn={() => setStep("role")}
          onSubmit={onSubmitUserDepartment}
        />
      ) : (
        <></>
      )}
    </PageDS>
  );
};
